// ════════════════════════════════════════════════════════════
//  ui/ChallengerStrip.jsx — a player (or partner) in the League, for real (online/challengers.js)
// ════════════════════════════════════════════════════════════
//  caP'n 2026-10-21: "once you join you're official" · "players can just jump right in at any point" ·
//  cups: "they must sign in the 10 minutes prior". Replaces the guests' "Take a seat" strip.
//  One quiet strip on the League's stage, only saying what matters right now:
//   · not a member  → "Join the League" (once, forever — you start in the Contenders)
//   · a member      → your place + the "I'm in" switch (on the cards while it's on), your next fight → ringside
//   · a cup's check-in is open (the 10 minutes before its card goes up) → "Check in" for it
//  Detail stays one tap away (the Empire never drowns you in numbers).
//  useChallengerKeeper (App.js): keeps "I'm in" fresh while it's on, and pays your wins in King's Krests.
// ════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from "react";
import { fbNet as net } from "../online/net.js";
import { serverNow, fmtKrests, deltaKrests } from "../online/ie.js";
import { join, setReady, checkIn, checkInWindow, entriesPath, collectChallengerPay, chKey, READY_EVERY, CH_PURSE } from "../online/challengers.js";
import { ledgerOf } from "../online/ledger.js";
import { racingDayOf, weekOf, rankKeys, statOf, CUPS, QUALIFY, checkInsOfDay, divRec, REC, saturdayOf, weekOfDay } from "../online/circuit.js";
import { markPath, partnerOf } from "../markData";

const CUP_SHORT = { k: "Contenders Cup", c: "Circuit Cup", x: "Champions Cup", l: "Losers' Cup", f: "the Finale", n: "tonight's cups", s: "Salvation" };
// a check-in's name ("n" = a V3 cup night: all five cups at once)
const entryName = (c) => (c === "n" ? "tonight's cups" : CUPS[c] ? CUPS[c].name : c);
const localTime = (ms) => new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
// "I'm in" is remembered per ACCOUNT (a second account on this browser doesn't inherit it) and kept the same in every tab
const IMIN = (uid) => `shatterproof.imIn.${uid}`;
const readIn = (uid) => { if (!uid) return false; try { return localStorage.getItem(IMIN(uid)) === "1"; } catch { return false; } };
const inSubs = new Set();
function setImIn(uid, v) { if (!uid) return; try { localStorage.setItem(IMIN(uid), v ? "1" : "0"); } catch { /* this tab only */ } for (const f of inSubs) f(); }
function useImIn(uid) {
  const [, bump] = useState(0);
  useEffect(() => {
    const f = () => bump((n) => n + 1);
    const st = (e) => { if (!e.key || e.key === IMIN(uid)) f(); };   // another tab flipped it
    inSubs.add(f); window.addEventListener("storage", st);
    return () => { inSubs.delete(f); window.removeEventListener("storage", st); };
  }, [uid]);
  return readIn(uid);
}
const hasPartner = async (uid) => { try { return !!partnerOf({ partner: await net.get(`${markPath(uid)}/partner`) }); } catch { return false; } };

/**
 * App-wide (every screen): while "I'm in" is on, re-stamp it every READY_EVERY so the next card catches you — and switch
 * it off on the server when it's switched off. Also pays my Challenger wins (King's Krests, once each). My wallet only.
 */
export function useChallengerKeeper(on, team, tomes, toast) {
  const uid = on && on.writable ? on.authUid : null;
  const v = useImIn(uid);
  const was = useRef(null);
  useEffect(() => {
    if (!uid) return undefined;
    let alive = true;
    const ping = async () => {
      if (!alive) return;
      if (!v) { if (was.current === true) await setReady(net, { uid, on: false }); was.current = false; return; }   // only on the switch-off, never a write per load
      was.current = true;
      const r = await setReady(net, { uid, on: true, team, tomes, name: on.name, partner: await hasPartner(uid), now: serverNow() });
      if (!r.ok && alive) { setImIn(uid, false); toast && toast(r.reason); }
    };
    ping();
    const id = v ? setInterval(ping, READY_EVERY) : null;
    return () => { alive = false; if (id) clearInterval(id); };
  }, [uid, v, (team || []).join(), (tomes || []).join()]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!uid) return undefined;
    let alive = true, running = false;
    const go = async () => {
      if (running || !alive) return; running = true;
      try {
        const w = weekOf(serverNow());
        const got = await collectChallengerPay(net, uid, [w - 1, w], (x) => ledgerOf(net).rows(x), (n) => deltaKrests(uid, n));
        if (got && alive && toast) toast(`+${fmtKrests(got)} King's Krest${got === 1 ? "" : "s"} for your League win${got / CH_PURSE === 1 ? "" : "s"}`);
      } catch { /* next pass */ } finally { running = false; }
    };
    const first = setTimeout(go, 8000), id = setInterval(go, 60000);
    return () => { alive = false; clearTimeout(first); clearInterval(id); };
  }, [uid, toast]);
}

export default function ChallengerStrip({ st, C, on, team, tomes, onPickTeam, onRingside, toast }) {
  const v = useImIn(on && on.authUid);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState({});   // cup → true once my check-in is in
  const me = on && on.authUid, key = me ? chKey(me) : null;
  const member = !!(C && C.members && key && C.members.has(key));
  const now = serverNow(), day = racingDayOf(now);
  // the cups whose check-in is open right now (the 10 minutes before their card goes up) — and that I can be drawn into:
  // the Contenders Cup is the Contenders', the Circuit Cup the Circuit's; Saturday's (and the Finale) are anyone's
  const myDiv = C && C.league && key ? (C.league.circuit.includes(key) ? "c" : "k") : null;
  // V3 (caP'n 2026-10-22: "any time, all cups too you can register early"): every check-in still open THIS WEEK — tonight's and the
  // nights to come — one tap enters them all
  const weekEnd = saturdayOf(weekOfDay(day));
  const openCups = [];
  for (let d = day; d <= weekEnd; d++) for (const c of checkInsOfDay(d)) {
    if (c === "k" || c === "c" ? c !== myDiv : false) continue;
    const w = checkInWindow(c, d);
    if (w && now >= w.open && now < w.close) openCups.push({ c, d, w, key: `${c}_${d}` });
  }
  const openKeys = openCups.map((x) => x.key).join();
  useEffect(() => {
    if (!openKeys || !me) return undefined;
    let alive = true;
    for (const x of openCups) net.get(`${entriesPath(x.c, x.d)}/${me}`).then((e) => { if (alive && e) setChecked((y) => ({ ...y, [x.key]: true })); }).catch(() => {});
    return () => { alive = false; };
  }, [openKeys, me]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!me || !C || !C.league) return null;
  const three = Array.isArray(team) && team.length === 3;
  if (!member) {
    const doJoin = async () => {
      if (!on.writable) { toast && toast("Sign in to join the League."); return; }
      setBusy(true);
      const r = await join(net, { uid: me, name: on.name, partner: await hasPartner(me), now: serverNow() });
      setBusy(false);
      toast && toast(!r.ok ? r.reason : r.already ? "You're already in the League — the board will catch up in a moment." : "You're in the League — a Challenger, starting in the Contenders. Switch \"I'm in\" on to get on the cards.");
    };
    return (
      <div className="gs">
        <span><b>Join the League</b> · a Challenger for real — every fight counts on the table · you start in the Contenders</span>
        <button className="btn tiny primary" disabled={busy} onClick={doJoin}>Join ▸</button>
      </div>
    );
  }
  // where I stand
  const div = C.league.circuit.includes(key) ? "circuit" : "contenders";
  const rec = C.v3 ? divRec(div === "circuit" ? "c" : "k") : REC.week, [won, lost] = rec(statOf(C.S, key));
  const place = rankKeys(C.league[div], C.S, rec).indexOf(key) + 1, played = won + lost;
  const pts = C.points ? C.points.get(key) : null;
  // my next fight on the stage
  const all = st ? [st.on, ...(st.up || [])].filter(Boolean) : [];
  const mine = all.find((f) => (f.A && f.A.key === key) || (f.B && f.B.key === key));
  const flip = () => { if (!v && !three) { onPickTeam && onPickTeam(); return; } setImIn(me, !v); toast && toast(!v ? "You're in — you'll be on the next card that goes up (:50)." : "You're out — no more cards until you switch back in."); };
  const doCheckIn = async (list) => {
    if (!three) { onPickTeam && onPickTeam(); return; }
    setBusy(true);
    const p = await hasPartner(me);
    let n = 0, why = null;
    for (const x of list) {
      const r = await checkIn(net, { c: x.c, day: x.d, uid: me, team, tomes, name: on.name, partner: p, now: serverNow() });
      if (r.ok) { n++; setChecked((y) => ({ ...y, [x.key]: true })); } else why = r.reason;
    }
    setBusy(false);
    toast && toast(n ? `Checked in for ${n === 1 ? entryName(list[0].c) : `${n} cup nights`} — with the three you have now.` : why);
  };
  const todo = openCups.filter((x) => !checked[x.key]), tonight = openCups.filter((x) => x.d === day);
  const qual = played >= QUALIFY;
  return (
    <div className={"gs" + (mine ? " mine" : "")}>
      <span>
        <b>{div === "circuit" ? "The Circuit" : "The Contenders"}</b>{place > 0 ? ` · #${place}` : ""} · {won}–{lost}{!qual && <i> · {QUALIFY - played} more to qualify</i>}{pts && C.line != null && <> · {Math.round(pts.score).toLocaleString()} pts{pts.score >= C.line ? " — over the Salvation line" : ""}</>}
        {mine && <> · <b>your fight</b> vs {(mine.A.key === key ? mine.B : mine.A).name}{mine.startAt ? ` · ${localTime(mine.startAt)}` : mine.estAt ? ` · ~${localTime(mine.estAt)}` : ""}</>}
        {tonight.map((x) => (checked[x.key] ? <span key={x.key}> · ✓ in {entryName(x.c)}</span> : <span key={x.key}> · <b>{entryName(x.c).replace(/^the /, "The ").replace(/^tonight/, "Tonight")}</b> check-in closes {localTime(x.w.close)}</span>))}
      </span>
      {mine && (mine.phase === "soon" || mine.phase === "prep" || mine.phase === "live") && <button className="btn tiny primary" onClick={() => onRingside(mine.id)}>Go ringside ▸</button>}
      {todo.length > 0 && <button className="btn tiny primary" disabled={busy} onClick={() => doCheckIn(todo)} title="Enter every cup still open this week (you can do it any time before each one's brackets go up)">{three ? (todo.length === 1 ? `Check in · ${CUP_SHORT[todo[0].c]} ▸` : `Check in · this week's cups (${todo.length}) ▸`) : "Pick your three ▸"}</button>}
      <button className={"btn tiny " + (v ? "primary" : "ghost")} title={"While this is on, you're on every card that goes up (:50). Your partner fights if you don't show."} onClick={flip}>{v ? "I'm in ✓" : "I'm in"}</button>
    </div>
  );
}
